import { Theme } from '@mui/material/styles';
import {
  ExportType,
  ExportFormat,
  TableExportPdf,
  TableExportXlsx,
  TablesExportPdf,
  TablesExportXlsx,
} from '../../../exports';
import { ColumnHandler } from './ColumnHandler';
import { RowHandler } from './RowHandler';
import type { Column } from './ColumnHandler';

type ExportMapper = {
  [K in ExportFormat]: (name: string) => ExportType<K>;
};

export type ExportConfig = Partial<{
  [K in ExportFormat]:
    | boolean
    | { generator: (table: ExportType<K>, fileName: string) => void; isDispatchAction: boolean };
}>;

const exportsDefault: {
  [K in ExportFormat]: (fileName: string) => (table: ExportType<K>, theme: Theme) => void;
} = {
  PDF: fileName => (table, theme) => {
    const tablesExport = new TablesExportPdf(theme);
    tablesExport.addTable('table', 1, table);
    tablesExport.export(fileName);
  },
  XLSX: fileName => async (table, theme) => {
    await TablesExportXlsx(fileName)(table, theme);
  },
};

export class ExportHandler<TData, TContext> {
  #columnHandler: ColumnHandler<TData, TContext>;

  #rowHandler: RowHandler<TData, TContext>;

  #exportConfig: ExportConfig;

  #exportMapper: ExportMapper;

  #context?: TContext;

  #exportColumnPredicate?: (col: Column<TData>) => boolean;

  #exportColumnSorter?: (a: Column<TData>, b: Column<TData>) => number;

  setExportColumnPredicate(fn: (col: Column<TData>, ctx: TContext) => boolean) {
    this.#exportColumnPredicate = (col: Column<TData>) => {
      const ctx = this.#columnHandler.getContext();
      return fn(col, ctx);
    };
  }

  setExportColumnSorter(fn: (a: Column<TData>, b: Column<TData>, ctx: TContext) => number) {
    this.#exportColumnSorter = (a: Column<TData>, b: Column<TData>) => {
      const ctx = this.#columnHandler.getContext();
      return fn(a, b, ctx);
    };
  }

  private getExportColumns() {
    const all = this.#columnHandler.columns;
    const filtered = this.#exportColumnPredicate ? all.filter(this.#exportColumnPredicate) : all;
    return this.#exportColumnSorter ? [...filtered].sort(this.#exportColumnSorter) : filtered;
  }

  constructor(
    columnHandler: ColumnHandler<TData, TContext>,
    rowHandler: RowHandler<TData, TContext>,
    exportConfig?: ExportConfig
  ) {
    this.#columnHandler = columnHandler;
    this.#rowHandler = rowHandler;
    this.#exportConfig = exportConfig || {};
    this.#exportMapper = {
      XLSX: (name: string) => this.tableToXlsx(name),
      PDF: (name: string) => this.tableToPdf(name),
    };
  }

  // llamado desde DataGridHandler.setContext()
  setContext(ctx: TContext) {
    this.#context = ctx;
  }

  private tableToXlsx(sheetName: string): TableExportXlsx {
    const columns = this.getExportColumns();
    const headers = columns.map(c => c.getTitle());

    const rows: TableExportXlsx['rows'] = this.#rowHandler.getRows(false).map(r =>
      columns.map(c => {
        const raw = c.getValue(r.item);
        return c.type === 'number' ? raw : c.getValueFormatted(raw);
      })
    );

    const currentTotals = this.#rowHandler.totals;
    const totals = columns.map(c => {
      const t = currentTotals.find(x => x.columnId === c.id);
      return t ? t.value : null;
    });

    return { headers, rows, sheetName, totals };
  }

  private tableToPdf(title: string): TableExportPdf {
    const columns = this.getExportColumns();
    const headers = columns.map(c => c.getTitle());
    const columnConfig = columns.map(c => ({ width: c.getWidth().pt, align: c.align }));
    const rows: TableExportPdf['rows'] = this.#rowHandler
      .getRows(false)
      .map(r => columns.map(c => c.getValueFormatted(c.getValue(r.item))));
    const currentTotals = this.#rowHandler.totals;
    const totals = columns.map(c => {
      const t = currentTotals.find(x => x.columnId === c.id);
      return t ? c.getValueFormatted(t.value) : '';
    });

    return { headers, rows, title, columnConfig, totals: totals.some(x => x) ? totals : [] };
  }

  setConfig(config: ExportConfig) {
    this.#exportConfig = config;
  }

  async export(format: ExportFormat, title: string, fileName: string, theme: Theme) {
    const exportValue = this.#exportConfig[format];
    if (exportValue === false) throw new Error(`La exportación a ${format} no está habilitada`);

    const table = this.#exportMapper[format](title) as never;

    if (exportValue === undefined || exportValue === true) {
      return exportsDefault[format](fileName)(table, theme);
    }
    return exportValue.generator(table, title);
  }

  hasDispatchAction(format: ExportFormat) {
    const exportValue = this.#exportConfig[format];
    if (!exportValue || exportValue === true) return false;
    return exportValue.isDispatchAction;
  }
}
